import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { Alert, Form, Space, message } from 'antd';

import { useQueryClient } from '@tanstack/react-query';

import type { Appointment } from '@erp/appointments/api/appointments.api';
import {
  type AppointmentHistoryEntry,
  type AppointmentServiceLine,
  useAppointmentHistory,
  useCreateAppointmentHistoryEntry,
  useDeleteAppointmentHistoryEntry,
  useUpdateAppointmentHistoryEntry,
  appointmentsExpandedQueryKey,
  appointmentsListKey,
} from '@erp/appointments/api/appointments.api';
import type { ClinicalRecord } from '@erp/clinical-history/api/clinicalHistory.api';
import { useCustomerClinicalRecords } from '@erp/clinical-history/api/clinicalHistory.api';
import { useAppointmentPermissions } from '@erp/appointments/hooks/useAppointmentPermissions';
import type { StaffDirectoryEntry } from '@erp/services/api/serviceStaff';
import { useAppSelector } from '@store/store';
import {
  selectIsSuperuser,
  selectUserGroups,
  selectUserPermissions,
} from '@store/slices/userSlice.tsx';
import { detectUserProfile } from '@erp/overview/erpSections.ts';
import dayjs from 'dayjs';

import ClinicalRecordHistoryModal from './history/ClinicalRecordHistoryModal';
import HistoryEntryFormModal from './history/HistoryEntryFormModal';
import RecentClinicalRecordsTable from './history/RecentClinicalRecordsTable';
import {
  buildEntryInitialValues,
  buildHistoryDraft,
  buildHistoryStaffLabel,
  buildStaffOptions,
  clinicalRecordToHistoryEntry,
  hasClinicalHistoryNote,
  mergeStaffOptions,
  type HistoryFormValues,
  type StaffOption,
} from './history/historyHelpers';

interface AppointmentHistorySectionProps {
  appointmentId: number;
  appointments: Appointment[];
  staffDirectory: StaffDirectoryEntry[];
  defaultStaffId?: number | null;
  onOpenAppointment?: (appointmentId: number) => void;
  autoStartCreate?: number;
  customerId?: number | null;
  spaceNameById?: Map<number, string>;
}

const isValidationError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'errorFields' in error;

export function AppointmentHistorySection({
  appointmentId,
  appointments: _appointments,
  staffDirectory,
  defaultStaffId,
  autoStartCreate,
  customerId,
  spaceNameById,
}: AppointmentHistorySectionProps): JSX.Element {
  const queryClient = useQueryClient();
  const [isFormModalOpen, setFormModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<AppointmentHistoryEntry | null>(null);
  const [viewingRecord, setViewingRecord] = useState<ClinicalRecord | null>(null);
  const [extraStaffOption, setExtraStaffOption] = useState<StaffOption | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [editingServiceLines, setEditingServiceLines] = useState<AppointmentServiceLine[] | undefined>(undefined);
  const [form] = Form.useForm<HistoryFormValues>();

  const {
    canViewHistory,
    canManageHistory,
    canManageHistoryOwn,
    canDeleteHistory,
  } = useAppointmentPermissions();
  const currentStaffId = useAppSelector((state) => state.user.profile?.staff_user_id ?? null);
  const [showAllHistory, setShowAllHistory] = useState(false);

  const userPermissions = useAppSelector(selectUserPermissions);
  const userGroups = useAppSelector(selectUserGroups);
  const isSuperuser = useAppSelector(selectIsSuperuser);

  const isProfessional = useMemo(() => {
    if (isSuperuser) return false;
    const permissionSet = new Set(userPermissions);
    const profile = detectUserProfile(permissionSet, userGroups);
    return profile === 'professional';
  }, [isSuperuser, userPermissions, userGroups]);

  const historyQuery = useAppointmentHistory(appointmentId, {
    staleTime: 30_000,
    enabled: canViewHistory,
  });
  const clinicalRecordsQuery = useCustomerClinicalRecords(customerId ?? null, {
    pageSize: 10,
    enabled: canViewHistory && Boolean(customerId),
  });

  const clinicalRecords = useMemo(
    () => clinicalRecordsQuery.data?.items ?? [],
    [clinicalRecordsQuery.data?.items]
  );
  const hasAnyRecords = clinicalRecords.length > 0;

  const filteredClinicalRecords = useMemo(() => {
    let list = clinicalRecords;

    // 1. Mostrar por defecto solo los turnos con historia clínica cerrada
    if (!showAllHistory) {
      list = list.filter((record) => hasClinicalHistoryNote(record));
    }

    // 2. Para el profesional, solo mostrar los turnos con fecha igual o anterior al día actual,
    // o futuros si tienen algo agregado y es un turno de dicho profesional (del mismo)
    if (isProfessional) {
      const today = dayjs();
      list = list.filter((record) => {
        const isPastOrToday = dayjs(record.start_at).isSameOrBefore(today, 'day');
        if (isPastOrToday) {
          return true;
        }
        // Turno futuro
        const hasHistory = hasClinicalHistoryNote(record);
        const isOwn = record.staff_user_id === currentStaffId;
        return hasHistory && isOwn;
      });
    }

    return list;
  }, [clinicalRecords, showAllHistory, isProfessional, currentStaffId]);
  const createMutation = useCreateAppointmentHistoryEntry();
  const updateMutation = useUpdateAppointmentHistoryEntry();
  const deleteMutation = useDeleteAppointmentHistoryEntry();

  const isOwnAppointment =
    defaultStaffId != null && currentStaffId != null && defaultStaffId === currentStaffId;
  const canCreateEntry = canManageHistory || (canManageHistoryOwn && isOwnAppointment);
  const canEditEntries = canDeleteHistory;
  const canDeleteEntries = canDeleteHistory;

  const baseStaffOptions = useMemo(
    () => buildStaffOptions(staffDirectory),
    [staffDirectory],
  );
  const staffOptions = useMemo(
    () => mergeStaffOptions(baseStaffOptions, extraStaffOption),
    [baseStaffOptions, extraStaffOption],
  );

  const ensureStaffOption = useCallback(
    (staffId?: number | null, entry?: AppointmentHistoryEntry) => {
      if (staffId == null) {
        setExtraStaffOption(null);
        return;
      }

      const exists = staffDirectory.some((staff) => staff.id === staffId);
      setExtraStaffOption(
        exists
          ? null
          : {
              value: staffId,
              label: buildHistoryStaffLabel(staffId, staffDirectory, entry),
            },
      );
    },
    [staffDirectory],
  );

  const closeFormModal = useCallback(() => {
    setFormModalOpen(false);
    setEditingEntry(null);
    setEditingServiceLines(undefined);
    setExtraStaffOption(null);
    form.resetFields();
  }, [form]);

  const handleStartCreate = useCallback(() => {
    if (!canCreateEntry) {
      message.error('No tenés permisos para registrar historial');
      return;
    }

    const normalizedDefault = defaultStaffId ?? staffOptions[0]?.value ?? undefined;
    setEditingEntry(null);

    // Find the current appointment's service lines
    const mainApp = _appointments?.find((a) => a.id === appointmentId);
    const mainRecord = clinicalRecords.find((r) => r.id === appointmentId);
    setEditingServiceLines(mainApp?.service_lines ?? mainRecord?.service_lines);

    ensureStaffOption(defaultStaffId);
    form.setFieldsValue({
      staff_user_id: normalizedDefault,
      notes: undefined,
    });
    setFormModalOpen(true);
  }, [canCreateEntry, defaultStaffId, ensureStaffOption, form, staffOptions, _appointments, appointmentId, clinicalRecords]);

  useEffect(() => {
    if (autoStartCreate) {
      handleStartCreate();
    }
  }, [autoStartCreate, handleStartCreate]);

  const handleEditEntry = useCallback(
    (entry: AppointmentHistoryEntry, serviceLines?: AppointmentServiceLine[]) => {
      if (!canEditEntries) {
        message.error('No tenés permisos para editar registros');
        return;
      }

      setEditingEntry(entry);
      setEditingServiceLines(serviceLines);
      ensureStaffOption(entry.staff_user_id, entry);
      form.setFieldsValue(buildEntryInitialValues(entry));
      setFormModalOpen(true);
    },
    [canEditEntries, ensureStaffOption, form],
  );

  const handleEditClinicalRecord = useCallback(
    (record: ClinicalRecord) => {
      if (!record.latest_history_entry_id) {
        return;
      }
      handleEditEntry(clinicalRecordToHistoryEntry(record), record.service_lines);
      setViewingRecord(null);
    },
    [handleEditEntry],
  );

  const handleDeleteEntry = useCallback(
    async (entry: AppointmentHistoryEntry) => {
      if (!canDeleteEntries) {
        message.error('No tenés permisos para eliminar registros');
        return;
      }

      try {
        setPendingDeleteId(entry.id);
        await deleteMutation.mutateAsync({
          appointmentId: entry.appointment_id,
          entryId: entry.id,
        });
        message.success('Registro eliminado');
        void clinicalRecordsQuery.refetch();
        void queryClient.invalidateQueries({ queryKey: appointmentsExpandedQueryKey });
        void queryClient.invalidateQueries({ queryKey: appointmentsListKey });
      } catch {
        message.error('No pudimos eliminar el registro, intentá nuevamente');
      } finally {
        setPendingDeleteId(null);
      }
    },
    [canDeleteEntries, clinicalRecordsQuery, deleteMutation, queryClient],
  );

  const handleDeleteClinicalRecord = useCallback(
    async (record: ClinicalRecord) => {
      if (!record.latest_history_entry_id) {
        return;
      }

      await handleDeleteEntry(clinicalRecordToHistoryEntry(record));
      setViewingRecord(null);
    },
    [handleDeleteEntry],
  );

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      if (!values.staff_user_id) {
        message.error('No hay profesional seleccionado');
        return;
      }

      const payload = buildHistoryDraft(values);
      if (editingEntry) {
        if (!canEditEntries) {
          message.error('No tenés permisos para editar registros');
          return;
        }

        await updateMutation.mutateAsync({
          appointmentId: editingEntry.appointment_id,
          entryId: editingEntry.id,
          payload,
        });
        message.success('Registro actualizado');
      } else {
        if (!canCreateEntry) {
          message.error('No tenés permisos para registrar historial');
          return;
        }

        await createMutation.mutateAsync({
          appointmentId,
          payload,
        });
        message.success('Nota registrada en el historial');
      }

      closeFormModal();
      void clinicalRecordsQuery.refetch();
      void queryClient.invalidateQueries({ queryKey: appointmentsExpandedQueryKey });
      void queryClient.invalidateQueries({ queryKey: appointmentsListKey });
    } catch (error) {
      if (!isValidationError(error)) {
        message.error('No pudimos guardar la nota, intentá nuevamente');
      }
    }
  }, [
    appointmentId,
    canCreateEntry,
    canEditEntries,
    clinicalRecordsQuery,
    closeFormModal,
    createMutation,
    editingEntry,
    form,
    updateMutation,
    queryClient,
  ]);

  if (!canViewHistory) {
    return (
      <Alert
        type="warning"
        title="No tenés permisos para ver el historial de atención"
        showIcon
      />
    );
  }

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="middle">
      <RecentClinicalRecordsTable
        records={filteredClinicalRecords}
        loading={clinicalRecordsQuery.isLoading}
        appointmentId={appointmentId}
        spaceNameById={spaceNameById}
        onOpenRecord={setViewingRecord}
        showAllHistory={showAllHistory}
        onShowAllHistoryChange={setShowAllHistory}
        hasAnyRecords={hasAnyRecords}
      />

      {historyQuery.isError ? (
        <Alert
          type="error"
          title="No pudimos cargar el historial"
          description={historyQuery.error?.message}
          showIcon
        />
      ) : null}

      <ClinicalRecordHistoryModal
        record={viewingRecord}
        canEdit={canEditEntries}
        canDelete={canDeleteEntries}
        deleteLoading={
          viewingRecord?.latest_history_entry_id === pendingDeleteId && deleteMutation.isPending
        }
        spaceNameById={spaceNameById}
        onClose={() => setViewingRecord(null)}
        onEdit={handleEditClinicalRecord}
        onDelete={handleDeleteClinicalRecord}
      />

      <HistoryEntryFormModal
        open={isFormModalOpen}
        editingEntry={editingEntry}
        form={form}
        staffOptions={staffOptions}
        confirmLoading={editingEntry ? updateMutation.isPending : createMutation.isPending}
        onCancel={closeFormModal}
        onSubmit={handleSubmit}
        serviceLines={editingServiceLines}
      />
    </Space>
  );
}
